import externalAxios from 'axios';
import axios from './axios';

class UploadUrlsError extends Error {
    constructor() {
        super('Invalid upload URLs response');
        this.name = 'UploadUrlsError';
    }
}

function assertUploadUrls(uploadUrls, expectedCount) {
    if (!Array.isArray(uploadUrls) || uploadUrls.length !== expectedCount) {
        throw new UploadUrlsError();
    }
    const invalid = uploadUrls.some(
        (url) => typeof url !== 'string' || !/^https:\/\//i.test(url),
    );
    if (invalid) {
        throw new UploadUrlsError();
    }
}

async function postImageWithRetry(file, url, retries = 0) {
    const formData = new FormData();
    formData.append('file', file);
    try {
        return await externalAxios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
        });
    } catch {
        if (retries < 3) {
            return postImageWithRetry(file, url, retries + 1);
        }
        return null;
    }
}

/**
 * Upload a single image file to Cloudflare Images via the property upload-url endpoint.
 * @returns {{ url: string, cloudflareId: string }}
 */
export async function uploadPhotoToCloudflare(file) {
    const urlRes = await axios.get('/property/image-upload-url/1');
    assertUploadUrls(urlRes.data, 1);
    const result = await postImageWithRetry(file, urlRes.data[0]);
    if (!result) {
        throw new Error('Upload failed');
    }
    return {
        url: result.data.result.variants[0],
        cloudflareId: result.data.result.id,
    };
}

/**
 * Run async tasks with a concurrency cap (p-limit equivalent without extra dependency).
 */
export async function runWithConcurrencyLimit(items, limit, fn) {
    if (!items.length) return [];
    const results = new Array(items.length);
    let nextIndex = 0;

    async function worker() {
        while (nextIndex < items.length) {
            const i = nextIndex;
            nextIndex += 1;
            results[i] = await fn(items[i], i);
        }
    }

    const workers = Array.from(
        { length: Math.min(limit, items.length) },
        () => worker(),
    );
    await Promise.all(workers);
    return results;
}

export { UploadUrlsError, assertUploadUrls };
